import React, {useContext, useEffect, useMemo, useState} from "react";
import {Table, TableBody, TableCell, TableHead, TableRow} from "@material-ui/core";
import AnnualReportTableCell from "../common/AnnualReportTableCell";
import FormTranslationContext from "context/FormTranslationContext";
import PropTypes from "prop-types";
import Tr from "translations/Translations";
import WorkableIconExpandButton from "components/button/WorkableIconExpandButton";
import WorkableIconDeleteButton from "components/button/WorkableIconDeleteButton";
import SwapVerticalCircleIcon from '@material-ui/icons/SwapVerticalCircle';
import WorkableIconButton from "components/button/WorkableIconButton";
import {getMoveAnnualReportEntryConfig} from "../common/MoveAnnualReportEntry";
import WorkableIconErrorButton from "components/button/WorkableIconErrorButton";
import {useParams} from "react-router";
import useSnackbar from "hooks/useSnackbar";
import WorkableIconEditButton from "components/button/WorkableIconEditButton";
import {getAnnualReportEditPopup} from "../common/AnnualReportEditPopup";
import IconButton from "components/button/IconButton";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import AnnualReportSteps from "../../constants/annualReportSteps";
import annualReportSteps from "../../constants/annualReportSteps";
import {simpleConfirmNoTranslate} from "libs/ConfirmNotification";
import AnnualReportsService from "services/AnnualReportsService";
import {useDidUpdateEffect} from "hooks/useDidUpdateEffect";
import clsx from "clsx";
import scrollTo from "libs/scrollTo";
import formatAmountNumber from "../../../../../../libs/formatAmountNumber";

function AnnualReportBelowEuTablePart(
    {
        type,
        classes,
        disabled,
        entries,
        search,
        setEditPopup,
        setMovePopup
    }
) {
    const params = useParams();
    const {formTr, dictTranslations} = useContext(FormTranslationContext)
    const [expanded, setExpanded] = useState(false);
    const [hasIncomplete, setHasIncomplete] = useState(false);
    const {enqueueSuccessSnackbar} = useSnackbar();

    const [entriesCounted, setEntriesCounted] = useState({
        worksCount: 0,
        worksAmount: 0,
        deliveriesCount: 0,
        deliveriesAmount: 0,
        servicesCount: 0,
        servicesAmount: 0,
    })

    const entriesFilteres = useMemo(() => {
        if (search.search) {
            return entries.filter((entry) => entry.number.includes(search.search))
        }
        return entries;
    }, [entries, search])

    useEffect(() => {
        let incomplete = false;
        setEntriesCounted(entries.reduce((acc, entry) => {
            incomplete = incomplete || !entry.complete;
            if (entry.orderType === "Services") {
                if (entry.amount)
                        acc.servicesAmount += entry.amount
                acc.servicesCount++
            }
            if (entry.orderType === "Delivery") {
                if (entry.amount)
                    acc.deliveriesAmount += entry.amount
                acc.deliveriesCount++
            }
            if (entry.orderType === "Works") {
                if (entry.amount)
                    acc.worksAmount += entry.amount
                acc.worksCount++
            }
            return acc;
        }, {worksCount: 0, worksAmount: 0, deliveriesCount: 0, deliveriesAmount: 0, servicesCount: 0, servicesAmount: 0,}))
        setHasIncomplete(incomplete);
    }, [entries])

    useDidUpdateEffect(() => {
        if (!expanded && search.search && entries.some((entry) => entry.number.includes(search.search))) {
            setExpanded(true);
        }
    }, [search])

    const doDelete = (entry) => {
        simpleConfirmNoTranslate(Tr("confirm_delete_annual_entry", {number: entry.number}), () => {
            AnnualReportsService.deleteAnnualReportTableEntry(params.id, entry.identity, AnnualReportSteps.belowEu, type).then((result) => {
                if (result) {
                    enqueueSuccessSnackbar(Tr("deleted") + " " + entry.number)
                }
            })
        })
    }

    const changeComplete = (entry, complete) => {
        const data = {
            ...entry,
            complete
        }
        AnnualReportsService.saveAnnualReportTableEntry(params.id, annualReportSteps.belowEu, data)
            .then((resp) => {
                if (resp) {
                    if (resp.success) {
                        enqueueSuccessSnackbar(Tr("saved") + " " + entry.number)
                    } else {
                        setEditPopup(getAnnualReportEditPopup(entry, false, true))
                    }
                }

            })
    }

    return (<>
            <TableRow className={classes.variantRow}>
                <AnnualReportTableCell borderLeft colSpan={6} onClick={() => setExpanded(!expanded)}>
                    {`${dictTranslations.procedures[type]} (${entriesFilteres.length}/${entries.length})`}
                </AnnualReportTableCell>
                <AnnualReportTableCell onClick={() => setExpanded(!expanded)}>
                    {hasIncomplete &&
                        <WorkableIconErrorButton
                            onClick={(e) => {
                                e.stopPropagation();
                                setExpanded(true);
                                const incompleteEntry = entriesFilteres.find(x => !x.complete);
                                if (incompleteEntry) {
                                    setTimeout((identity) => scrollTo(`#${annualReportSteps.belowEu}-${identity}`, 200), 100, incompleteEntry.identity)
                                }
                            }}
                            tooltip={Tr("annual_report_section_incomplete")}
                            className={classes.sectionErrorIcon}
                        />
                    }
                </AnnualReportTableCell>
                <AnnualReportTableCell borderLeft borderRight rowSpan={2}>
                    <WorkableIconExpandButton expanded={expanded} onClick={() => setExpanded(!expanded)}/>
                </AnnualReportTableCell>
            </TableRow>
            <TableRow className={classes.variantRow2}>
                <AnnualReportTableCell borderLeft borderRight colSpan={2}>
                    {dictTranslations.orderType["Services"] + ": "}
                    {entriesCounted.servicesCount}
                    {" | " + Tr("value") + ": "}
                    {formatAmountNumber(entriesCounted.servicesAmount) + " zł"}
                </AnnualReportTableCell>

                <AnnualReportTableCell borderRight colSpan={2}>
                    {dictTranslations.orderType["Delivery"] + ": "}
                    {entriesCounted.deliveriesCount}
                    {" | " + Tr("value") + ": "}
                    {formatAmountNumber(entriesCounted.deliveriesAmount) + " zł"}
                </AnnualReportTableCell>

                <AnnualReportTableCell colSpan={3}>
                    {dictTranslations.orderType["Works"] + ": "}
                    {entriesCounted.worksCount}
                    {" | " + Tr("value") + ": "}
                    {formatAmountNumber(entriesCounted.worksAmount) + " zł"}
                </AnnualReportTableCell>

            </TableRow>
            {expanded &&
                <TableRow>
                    <AnnualReportTableCell colSpan={8} borderLeft borderRight className={classes.demandsCell}>
                        <Table className={classes.demandsTable}>
                            <TableHead>
                                <TableRow>
                                    <AnnualReportTableCell>
                                        {formTr("mmia.belowEu.number")}
                                    </AnnualReportTableCell>
                                    <AnnualReportTableCell>
                                        {formTr("mmia.belowEu.orderType")}
                                    </AnnualReportTableCell>
                                    <AnnualReportTableCell>
                                        {formTr("mmia.belowEu.amount")}
                                    </AnnualReportTableCell>
                                    <TableCell className={classes.buttonColumn}></TableCell>
                                    <TableCell className={classes.buttonColumn}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {entriesFilteres.map((entry, index) => (
                                    <TableRow id={`${annualReportSteps.belowEu}-${entry.identity}`} key={index}
                                              className={clsx(classes.demandsRow, {complete: entry.complete, incomplete: !entry.complete})}>
                                        <AnnualReportTableCell>
                                            {entry.number}
                                        </AnnualReportTableCell>
                                        <AnnualReportTableCell>
                                            {dictTranslations.orderType[entry.orderType]}
                                        </AnnualReportTableCell>
                                        <AnnualReportTableCell>
                                            {formatAmountNumber(entry.amount)}
                                        </AnnualReportTableCell>
                                        <AnnualReportTableCell className={classes.buttonColumn}>
                                            <WorkableIconEditButton disabled={disabled} onClick={() => setEditPopup(getAnnualReportEditPopup(entry))}/>
                                            {
                                                entry.complete ?
                                                    <IconButton tooltip={Tr("mark_draft_version")} onClick={() => changeComplete(entry, false)}
                                                                Icon={CheckCircleOutlineIcon} disabled={disabled}/>
                                                    :
                                                    <IconButton tooltip={Tr("mark_saved_version")} onClick={() => changeComplete(entry, true)}
                                                                disabled={disabled} Icon={RadioButtonUncheckedIcon}/>
                                            }
                                        </AnnualReportTableCell>
                                        <AnnualReportTableCell className={classes.buttonColumn}>
                                            <WorkableIconButton
                                                tooltip={Tr("change_variant")} Icon={SwapVerticalCircleIcon} disabled={disabled}
                                                onClick={() => setMovePopup(getMoveAnnualReportEntryConfig(params.id, entry.identity, type))}/>
                                            <WorkableIconDeleteButton disabled={disabled} onClick={() => doDelete(entry)}
                                            />
                                        </AnnualReportTableCell>
                                    </TableRow>
                                ))}

                                {!entriesFilteres.length && <TableRow>
                                    <AnnualReportTableCell className={classes.noOptions} colSpan={5}>
                                        {Tr("no_options")}
                                    </AnnualReportTableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                    </AnnualReportTableCell>
                </TableRow>
            }

        </>
    );
}

AnnualReportBelowEuTablePart.propTypes = {
    type: PropTypes.string.isRequired,
    classes: PropTypes.object.isRequired,
    disabled: PropTypes.bool.isRequired,
    entries: PropTypes.array.isRequired,
    search: PropTypes.shape({
        search: PropTypes.string
    }).isRequired,
    setEditPopup: PropTypes.func.isRequired,
    setMovePopup: PropTypes.func.isRequired,
}

export default AnnualReportBelowEuTablePart;